import { Topic, TopicTab, FileItem } from "./types";
import Chatbot from "../../chatbot/Chatbot";
import TaskCreator from "../../taskcreator/TaskCreator";
import KahootTaskCreator from "../../taskcreator/KahootTaskCreator";

interface TopicViewProps {
    activeTopic: Topic | null;
    setActiveTopicId: (id: string | null) => void;
    deleteTopic: (topic: Topic) => void;
    activeTab: TopicTab;
    setActiveTab: (tab: TopicTab) => void;
    handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    uploading: boolean;
    uploadProgress: number;
    downloadFile: (file: FileItem) => void;
    course: any;
}

export default function TopicView({
                                      activeTopic,
                                      setActiveTopicId,
                                      deleteTopic,
                                      activeTab,
                                      setActiveTab,
                                      handleFileUpload,
                                      uploading,
                                      uploadProgress,
                                      downloadFile,
                                      course
                                  }: TopicViewProps) {
    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div className="mb-8">
                <div className="mb-4">
                    <button
                        onClick={() => setActiveTopicId(null)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 cursor-pointer transition-all hover:bg-gray-100 hover:text-gray-800 hover:border-green-500 hover:-translate-x-1"
                    >
                        <span className="text-xl">←</span>
                        Zurück zur Kursübersicht
                    </button>
                </div>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                        <h2 className="flex items-center gap-3 text-2xl lg:text-3xl font-bold mb-3 text-gray-800">
                            <span className="text-2xl">📚</span>
                            {activeTopic?.title}
                        </h2>
                        <div className="flex flex-wrap gap-4">
                            {activeTopic?.files && activeTopic.files.length > 0 && (
                                <span className="flex items-center gap-1.5 text-gray-500 text-sm">
                                    <span>📄</span>
                                    {activeTopic.files.length} Dateien
                                </span>
                            )}
                            <span className="flex items-center gap-1.5 text-gray-500 text-sm">
                                <span>🕒</span>
                                Zuletzt bearbeitet
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => activeTopic && deleteTopic(activeTopic)}
                            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold cursor-pointer transition-all hover:shadow-lg hover:shadow-red-500/20 hover:-translate-y-0.5"
                        >
                            🗑 Löschen
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-6 bg-gray-100/80 backdrop-blur-sm rounded-xl p-1.5 border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-1.5">
                    {(["files", "chat", "tasks", "kahoot"] as TopicTab[]).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                                activeTab === tab
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md'
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200/50'
                            }`}
                        >
                            <span>
                                {tab === "files" && "📂"}
                                {tab === "chat" && "🤖"}
                                {tab === "tasks" && "📝"}
                                {tab === "kahoot" && "🎮"}
                            </span>
                            <span>
                                {tab === "files" && "Materialien"}
                                {tab === "chat" && "AI Chatbot"}
                                {tab === "tasks" && "Aufgaben"}
                                {tab === "kahoot" && "Kahoot Quiz"}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <div className="animate-fadeIn">
                {/* Files Tab */}
                {activeTab === "files" && (
                    <div>
                        {/* Upload Zone */}
                        <div className="mb-8">
                            <label className="block w-full min-h-[200px] border-2 border-dashed border-gray-300 rounded-xl bg-gray-50/80 backdrop-blur-sm cursor-pointer transition-all hover:border-green-500 hover:bg-gray-100 hover:-translate-y-1 relative overflow-hidden">
                                <input
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={handleFileUpload}
                                    disabled={uploading}
                                />
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                                    <div className="text-4xl mb-3 text-green-500">📤</div>
                                    <div>
                                        <strong className="block text-lg text-gray-800 mb-1">
                                            Dateien hierher ziehen oder klicken
                                        </strong>
                                        <p className="text-gray-500">
                                            PDF, Word, PowerPoint, Bilder oder Textdateien
                                        </p>
                                    </div>
                                </div>
                                {uploading && (
                                    <div className="absolute bottom-0 left-0 right-0 h-2 bg-gray-200 overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300 relative overflow-hidden"
                                            style={{ width: `${uploadProgress}%` }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                                        </div>
                                        <div className="absolute top-3 right-4 text-green-600 font-semibold text-sm">
                                            {uploadProgress}% hochgeladen
                                        </div>
                                    </div>
                                )}
                            </label>
                        </div>

                        {/* Files Grid */}
                        {activeTopic?.files && activeTopic.files.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {activeTopic.files.map((file, index) => (
                                    <div
                                        key={index}
                                        className="bg-white border border-gray-200 rounded-xl p-4 flex gap-4 items-start transition-all hover:-translate-y-1 hover:shadow-lg hover:border-green-500/50"
                                    >
                                        <div className="text-3xl">
                                            {file.name.match(/\.(pdf)$/i) ? '📕' :
                                                file.name.match(/\.(docx?|txt)$/i) ? '📄' :
                                                    file.name.match(/\.(ppt|pptx)$/i) ? '📊' :
                                                        file.name.match(/\.(jpg|jpeg|png|gif)$/i) ? '🖼️' : '📎'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-gray-800 truncate mb-3">
                                                {file.name}
                                            </h4>
                                            <button
                                                onClick={() => downloadFile(file)}
                                                className="w-full py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium cursor-pointer transition-all hover:shadow-lg hover:shadow-blue-500/20"
                                            >
                                                📥 Herunterladen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <div className="text-5xl mb-4 opacity-50">📂</div>
                                <h3 className="text-xl font-medium text-gray-700 mb-2">
                                    Noch keine Dateien
                                </h3>
                                <p>
                                    Lade deine ersten Materialien hoch, um mit dem Chatbot zu arbeiten.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Chatbot Tab */}
                {activeTab === "chat" && activeTopic && (
                    <div>
                        <div className="text-center mb-8 p-6 bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200">
                            <h3 className="text-2xl font-bold mb-3 text-gray-800">
                                🤖 AI Chatbot für "{activeTopic.title}"
                            </h3>
                            <p className="text-gray-600 max-w-2xl mx-auto">
                                Stelle Fragen zum aktuellen Thema. Der Chatbot antwortet basierend auf den hochgeladenen Materialien.
                            </p>
                        </div>
                        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 p-6">
                            <Chatbot
                                topic={{
                                    id: activeTopic.id,
                                    title: activeTopic.title,
                                    files: activeTopic.files || [],
                                }}
                                showHeader={false}
                            />
                        </div>
                    </div>
                )}

                {/* Tasks Tab */}
                {activeTab === "tasks" && activeTopic && (
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 overflow-hidden">
                        <TaskCreator
                            defaultTopic={`${course.title} – ${activeTopic.title}`}
                            files={activeTopic.files || []}
                        />
                    </div>
                )}

                {/* Kahoot Tab */}
                {activeTab === "kahoot" && activeTopic && (
                    <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-200 overflow-hidden">
                        <KahootTaskCreator
                            defaultTopic={`${course.title} – ${activeTopic.title}`}
                            files={activeTopic.files || []}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}